import { useCallback } from "react";
import { onHeroAboutDownloadResumeClick } from "../analytics/analytics.click-handlers";
import Colors from "../assets/constants/Colors";
import { appLinks } from "../assets/constants/Strings";
import styled from "../styled-components";
import RotatingImage, { RImage } from "./RotatingImage";

//TODO: Remove props and put constants here or pipe in props from About me page.
/**
 * AboutHero (styled-components)
 *
 * Props:
 * - overline   (string) e.g., "About Me"
 * - title      (string) e.g., "I can develop that help people"
 * - body1       (string)
 * - body2       (string)
 * - body3       (string)
 * - imageSrc   (string)
 * - imageAlt   (string)
 * - stats      (Array<{ value: string, label: string }>) — 3 items recommended
 * - ctaLabel   (string) e.g., "Download My Resume"
 */


interface AboutHeroProps {
  overline?: string;
  title?: string;
  body1?: string;
  body2?: string;
  body3?: string;
  images?: RImage[];
  stats?: { value: string; label: string }[];
  ctaLabel?: string;
}

const AboutHero = ({
  overline = "About Me",
  title = "Software Engineer",
  body1 = `I am a computer engineer by trade, which means that I was coding, drawing circuits, and reading obscure documentation in university (UC Davis). That foundation gave me the curiosity, courage and persistence I bring to every project. Today, I apply that same mindset to every new project I contribute to.`,
  body2 = `In practice, I am a software engineer. I support web and mobile cross platform and native applications in React, React Native, and IOS.`,
  body3 = `Recently, I also code in Google Workspace with Apps Scripts (JS) and do Data Anaytics in Python and SQL.`,
  images = [],
  // stats = [
  //   { value: "100+", label: "Programs written" },
  //   { value: "10+", label: "Apps built" },
  //   { value: "500+", label: "Problems Solved" },
  // ],
  ctaLabel = "Download My Resume",
}: AboutHeroProps) => {

    const onResumeAndreaLopezDevClicked = useCallback(() => onHeroAboutDownloadResumeClick({
      href: appLinks.resumeAndreaLopezDev,
      targetText: ctaLabel
    }),[ctaLabel])

  return (
    <Section>
      <Grid>
        <Left>
          <RotatingImage images={images}></RotatingImage>
        </Left>

        <Right>
          <OverlineRow>
            <Overline>{overline}</Overline>
            <Rule aria-hidden="true" />
          </OverlineRow>

          <Title>{title}</Title>
          <Body>{body1}</Body>
          <Body>{body2}</Body>
          <Body>{body3}</Body>

          {/* {stats?.length > 0 && (
            <StatsRow>
              {stats.slice(0, 3).map((s, i) => (
                <Stat key={i}>
                  <StatValue>{s.value}</StatValue>
                  <StatLabel>{s.label}</StatLabel>
                </Stat>
              ))}
            </StatsRow>
          )} */}

            <Cta onClick={onResumeAndreaLopezDevClicked} href={appLinks.resumeAndreaLopezDev} target="_blank" rel="noreferrer">
              {ctaLabel}
            </Cta>
        </Right>
      </Grid>
    </Section>
  );
}

/* ========== styled ========== */

const Section = styled.section`
  padding: clamp(48px, 7vw, 96px) 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: clamp(24px, 5vw, 72px);
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;

  @media (max-width: 992px) {
    order: 1;
    justify-content: center;
  }
`;

const Person = styled.img`
  width: min(320px, 100%);
  height: auto;
  display: block;
  object-fit: contain;
  filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.06));
`;

const Right = styled.div`
  max-width: 640px;

  @media (max-width: 992px) {
    order: 2;
  }
`;

const OverlineRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 12px;
`;

const Overline = styled.span`
  color: #8a8a8a;
  font-size: 14px;
  letter-spacing: 0.4px;
`;

const Rule = styled.span`
  height: 2px;
  width: 53px;
  background: #e5e5e5;
  border-radius: 2px;
`;

const Title = styled.h2`
  margin: 0 0 14px;
  font-size: clamp(28px, 3.6vw, 40px);
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: -0.2px;
`;

const Body = styled.p`
  margin: 0 0 28px;
  color: #777;
  line-height: 1.7;
  font-size: 16px;
`;

// const StatsRow = styled.div`
//   display: grid;
//   grid-template-columns: repeat(3, auto);
//   gap: clamp(20px, 4vw, 56px);
//   margin: 12px 0 28px;

//   @media (max-width: 560px) {
//     grid-template-columns: 1fr;
//     gap: 16px;
//   }
// `;

// const Stat = styled.div``;

// const StatValue = styled.div`
//   font-weight: 800;
//   font-size: 20px;
//   margin-bottom: 4px;
// `;

// const StatLabel = styled.div`
//   color: #8a8a8a;
//   font-size: 14px;
// `;

const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 22px;
  border-radius: 999px;
  background: ${Colors.globalLink};
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 14.5px;
  box-shadow: 0 10px 22px ${Colors.globalHoverLink};
  transition: transform 150ms ease, box-shadow 150ms ease, filter 150ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 28px ${Colors.globalHoverLink};
    color: #fff;
    filter: brightness(1.02);
  }
`;

export default AboutHero;